<script setup lang="ts">
import {computed, ref} from "vue";
import {type ProductResponse} from "../../../responses/ProductResponse";
import {type ProductVariantResponse} from "../../../responses/ProductVariantResponse";
import {useProductStore} from "../../stores/product";
import {find, drop} from 'lodash';
import {type ProductImageResponse} from "../../../responses/ProductImageResponse";
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import VideoWrapper from "../VideoWrapper.vue";
import NoImageURL from "../../../../images/no-image.png";
import Carousel from "../../ui/carousel/Carousel.vue";
import CarouselItem from "../../ui/carousel/CarouselItem.vue";
import CarouselPrevious from "../../ui/carousel/CarouselPrevious.vue";
import CarouselNext from "../../ui/carousel/CarouselNext.vue";
import CarouselContent from "../../ui/carousel/CarouselContent.vue";
import type {CarouselProps} from "../../ui/carousel/interface.ts";

interface Props{
    product: ProductResponse
}
const props = defineProps<Props>();
const selectedImage = ref(props.product.images[0]);
const productStore = useProductStore();

productStore.events.on('selectedVariantChanged', async (variant: ProductVariantResponse) => {
    let variantImage = find(props.product.images, function(productImage: ProductImageResponse){
        return productImage.product_variant_id === variant?.id;
    });
    selectedImage.value = variantImage ?? props.product.images[0];
});

const isVideo = (image: ProductImageResponse | null | undefined): boolean => !!image?.video_id;
const videoSlideId = (image: ProductImageResponse): string => `product-${props.product.id}-video-slide-${image.id}`;
const videoImages = computed(() => props.product.images.filter((image) => isVideo(image)));

Fancybox.bind("[data-fancybox]", {
    Thumbs: false,
    videoAutoplay: false,
});
const carouselOpts: CarouselProps[] = {
    opts: {
        align: 'start',
        loop: true,
        dragFree: true,
    },
};
</script>
<template>
    <div class="w-full lg:w-1/2" v-if="product.images.length">
        <div class="w-full mb-4">
            <a :href="isVideo(selectedImage) ? `#${videoSlideId(selectedImage)}` : selectedImage.image_url" :data-type="isVideo(selectedImage) ? 'inline' : 'image'" data-fancybox="gallery">
                <img :alt="selectedImage.alt_text ?? product.name" :src="selectedImage.image_url" class="mx-auto w-fit" />
                <div class="flex flex-row absolute top-7 right-7 gap-2">
                    <span class="text-sm leading-5 px-3 py-1 font-semibold bg-primary text-white rounded-full" v-for="label in product.product_labels" v-text="label.label"></span>
                </div>
            </a>
        </div>
        <div >
            <Carousel class="w-full" arrow-color="transparent" outline-color="#d68f00" hover-arrow-color="#d68f00" :outline="true" :opts="carouselOpts.opts">
                <CarouselContent class="flex lg:-ml-2 lg:-mr-2">
                    <CarouselItem v-for="image in drop(product.images)" :key="image.id" class="basis-full sm:basis-1/2 lg:basis-1/3 shrink-0 px-0 lg:px-2 ">
                        <img
                            :alt="image.alt_text ?? product.name"
                            :src="image.image_url"
                            data-fancybox="gallery"
                            :data-type="isVideo(image) ? 'inline' : undefined"
                            :data-src="isVideo(image) ? `#${videoSlideId(image)}` : undefined"
                            class="cursor-pointer object-cover aspect-square w-full"
                        />
                    </CarouselItem>
                </CarouselContent>

                <CarouselPrevious svg-classes="lg:!w-12 lg:!h-12" class="lg:left-4"  />
                <CarouselNext svg-classes="lg:!w-12 lg:!h-12" class="lg:right-4" />
            </Carousel>
        </div>
    </div>
    <div class="w-full lg:w-1/2" v-else>
        <img :alt="product.name" :src="product.thumbnail?.image_path ?? NoImageURL" data-fancybox="gallery" class="mx-auto w-fit"  />
    </div>
    <div class="hidden" aria-hidden="true">
        <VideoWrapper
            v-for="image in videoImages"
            :key="image.id"
            :id="videoSlideId(image)"
            :video-id="image.video_id"
            :source="image.video_service"
            :autoplay="true"
            class="w-full max-w-[900px] aspect-video"
        />
    </div>
</template>
